import './archive.css';

import { useRecoilState, useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { allUserConfigsState } from '../../recoil/atoms/allUserConfigsState';
import { initialReportFiltersState, reportFiltersState } from '../../recoil/atoms/reportFiltersState';
import { useWindowSize } from '../../../common/hooks/WindowSize';

import ReplayIcon from '@mui/icons-material/Replay';

import { translate } from '../../../common/language/translations';
import { UserDropDown } from '../../components/controls/dropDowns/UserDropDown';
import { DropDownWithIcons } from '../../components/controls/dropDowns/DropDownWithIcons';
import { ReportType } from '../../types/report';
import { InputField } from '../../components/controls/input/InputField';
import { IconButton } from '../../components/controls/buttons/IconButton';
import { useMemo } from 'react';
import { UserConfig } from '../../types/user';
import { getSortDistanceAccess } from '../../utils/accessUtils';
import { isFokusUser } from '../../utils/userConfigUtils';
import { DropDown } from '../../components/controls/dropDowns/DropDown';
import { ageKeys } from '../../static/constants/scoutingFilters';
import { eventDataPositionOptions } from '../../static/constants/positions';
import { LeagueDropDown } from '../../components/controls/dropDowns/LeagueDropDown';
import { CountryDropDown } from '../../components/controls/dropDowns/CountryDropDown';
import { ReportRatingDropDown } from '../../components/controls/dropDowns/ReportRatingDropDown';
import { ReportRatingRequirements } from '../../types/navigation';
import { getCommonScoresForAllReportVersions } from '../../components/documents/report/reportVersions';


interface ReportFiltersProps {
  currentReportFilterExpanded: string | undefined;
  setCurrentReportFilterExpanded: (value: string | undefined) => void;
}


export const ReportFilters: React.FC<ReportFiltersProps> = ({
  currentReportFilterExpanded,
  setCurrentReportFilterExpanded
}) => {

  const userConfig = useRecoilValue(userConfigState);
  const allUsersWithSameTenant = useRecoilValue(allUserConfigsState);

  const { width } = useWindowSize();

  const [reportFilters, setReportFilters] = useRecoilState(reportFiltersState);

  const commonScoreKeys = getCommonScoresForAllReportVersions(userConfig?.club);


  const userDropDownOptions = useMemo(() => {
    let options: UserConfig[] = [];
    if (userConfig?.accessConfig.reportAccess === 'personal') {
      options = [userConfig];
    }
    else if (userConfig?.accessConfig.reportAccess === 'academy') {
      options = Object.values(allUsersWithSameTenant).filter((user) => user.accessConfig.reportAccess === 'academy');
    }
    else {
      options = Object.values(allUsersWithSameTenant).map((user) => (user));
    }

    // if the user is not a fokus user, we remove all fokus users as options
    if (!isFokusUser(userConfig)) {
      options = options.filter((user) => !isFokusUser(user));
    }

    options.sort((a, b) => getSortDistanceAccess(a, b));
    return options;
  }, [allUsersWithSameTenant, userConfig]);


  return (
    <div className='archive-report-filters-container'>

      <div className='archive-report-filters-row'>
        <div className='archive-report-filters-drop-down archive-report-filters-name'>
          <InputField
            value={reportFilters.nameSearchString}
            placeholder={translate(width < 1300 ? 'name' : 'searchForName', userConfig?.language)}
            onChange={(event) => setReportFilters({ ...reportFilters, nameSearchString: event.target.value })}
            resetValue={() => setReportFilters({ ...reportFilters, nameSearchString: '' })}
          />
        </div>

        <div className='archive-report-filters-drop-down-title archive-report-filters-report-type'>
          {translate('reportTypes', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-report-type'>
          <DropDownWithIcons
            id={'archive-report-filters-report-type'}
            dropDownOptions={['ScoutingReport', 'MatchReport', 'Comment', 'Attachment']}
            selectedOptions={reportFilters.selectedReportTypes}
            setSelectedOptions={(value: string[]) => setReportFilters({ ...reportFilters, selectedReportTypes: value as ReportType[] })}
            isDropDownExpanded={currentReportFilterExpanded === 'reportTypes'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'reportTypes' : undefined)}
            defaultDropDownText={translate('select', userConfig?.language)}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={2}
            maxHeight={'82vh'}
            multiSelect={true}
            useReportIcons={true}
          />
        </div>

        <div className='archive-report-filters-drop-down-title archive-report-filters-users'>
          {translate('users', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-users'>
          <UserDropDown
            id={'archive-report-filters-users'}
            userMap={allUsersWithSameTenant}
            dropDownOptions={userDropDownOptions}
            selectedOptions={userConfig?.accessConfig.reportAccess === 'personal' ? [userConfig.email] : reportFilters.selectedUsers}
            setSelectedOptions={(value: string[]) => setReportFilters({ ...reportFilters, selectedUsers: value })}
            isDropDownExpanded={currentReportFilterExpanded === 'users'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'users' : undefined)}
            defaultDropDownText={translate('select', userConfig?.language)}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={2}
            maxHeight={'82vh'}
            multiSelect={userConfig?.accessConfig.reportAccess !== 'personal'}
            dropDownDisabled={userConfig?.accessConfig.reportAccess === 'personal'}
          />
        </div>

        <div className='archive-report-filters-drop-down-title archive-report-filters-order-by'>
          {translate('orderBy', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-order-by'>
          <DropDown
            id={'archive-report-filters-order-by'}
            dropDownOptions={['date']}
            dropDownSecondCategoryOptions={commonScoreKeys}
            selectedOptions={reportFilters.selectedOrderBy}
            setSelectedOptions={(value: string[]) => setReportFilters({ ...reportFilters, selectedOrderBy: value as string[] })}
            isDropDownExpanded={currentReportFilterExpanded === 'orderBy'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'orderBy' : undefined)}
            defaultDropDownText={translate('select', userConfig?.language)}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={2}
            maxHeight={'82vh'}
            forceSelection={true}
            isReportScoreDropDown={true}
          />
        </div>

        <IconButton
          onClick={() => setReportFilters(initialReportFiltersState)}
          icon={<ReplayIcon style={{ fontSize: 20 }} />}
          size={24}
          style={{ position: 'absolute', marginTop: 41, right: 'calc(2.5% - 12px)' }}
          startWhite={true}
          title={translate('resetFilters', userConfig?.language)}
        />
      </div>

      <div className='archive-report-filters-row'>
        <div className='archive-report-filters-drop-down-title archive-report-filters-age'>
          {translate('maxAge', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-age'>
          <DropDown
            id={'archive-report-filters-age'}
            dropDownOptions={ageKeys}
            selectedOptions={reportFilters.selectedMaxAge}
            setSelectedOptions={(value: string[]) => setReportFilters({ ...reportFilters, selectedMaxAge: value })}
            isDropDownExpanded={currentReportFilterExpanded === 'maxAge'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'maxAge' : undefined)}
            defaultDropDownText={translate('select', userConfig?.language)}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={2}
            maxHeight={'calc(82vh - 80px)'}
          />
        </div>

        <div className='archive-report-filters-drop-down-title archive-report-filters-position'>
          {translate('positions', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-position'>
          <DropDown
            id={'archive-report-filters-position'}
            dropDownOptions={eventDataPositionOptions}
            selectedOptions={reportFilters.selectedPositions}
            setSelectedOptions={(value: string[]) => setReportFilters({ ...reportFilters, selectedPositions: value })}
            isDropDownExpanded={currentReportFilterExpanded === 'position'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'position' : undefined)}
            defaultDropDownText={translate('select', userConfig?.language)}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={2}
            maxHeight={'calc(82vh - 80px)'}
            multiSelect={true}
          />
        </div>

        <div className='archive-report-filters-drop-down-title archive-report-filters-league'>
          {translate('leagues', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-league'>
          <LeagueDropDown
            id={'archive-report-filters-league'}
            isDropDownExpanded={currentReportFilterExpanded === 'league'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'league' : undefined)}
            selectedLeagues={reportFilters.selectedLeagues ?? []}
            setSelectedLeagues={(selectedLeagues: string[]) => setReportFilters({ ...reportFilters, selectedLeagues: selectedLeagues })}
            defaultDropDownText={'select'}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={3}
            maxHeight={'calc(82vh - 80px)'}
          />
        </div>

        <div className='archive-report-filters-drop-down-title archive-report-filters-nationality'>
          {translate('nationalities', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-nationality'>
          <CountryDropDown
            id={'archive-report-filters-nationalities'}
            isDropDownExpanded={currentReportFilterExpanded === 'nationality'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'nationality' : undefined)}
            selectedCountryCodes={reportFilters.selectedNationalities ?? []}
            setSelectedCountryCodes={(selectedNationalities: string[]) => setReportFilters({ ...reportFilters, selectedNationalities: selectedNationalities })}
            defaultDropDownText={'select'}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={3}
            maxHeight={'calc(82vh - 80px)'}
          />
        </div>
      </div>

      <div className='archive-report-filters-row'>
        <div className='archive-report-filters-drop-down-title archive-report-filters-rating-requirements'>
          {translate('minimumReportScores', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-rating-requirements'>
          <ReportRatingDropDown
            id={'archive-report-filters-rating-requirements'}
            isDropDownExpanded={currentReportFilterExpanded === 'rating-requirements'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'rating-requirements' : undefined)}
            selectedOptions={reportFilters.selectedRatingRequirements ?? []}
            setSelectedOptions={(selectedOptions: ReportRatingRequirements[]) => setReportFilters({ ...reportFilters, selectedRatingRequirements: selectedOptions })}
            defaultDropDownText={'select'}
            defaultDropDownTextColor={'#00000080'}
            maxHeight={'calc(82vh - 160px)'}
          />
        </div>
      </div>

    </div>
  );
};
